import type { HashDone, HashFail, HashProgress } from './hash.worker';

type HashResult = { sha256: string; hash_kind: 'full' | 'sampled' };
type Job = { id: string; file: Blob; onProgress?: (p: number) => void; resolve: (r: HashResult) => void; reject: (e: Error) => void };
/**
 * Release 1 F5 — runs between hashing and the scheduler. `'skip'` turns the job into a `cancelled` rejection (the
 * upload manager treats that as a user cancel); anything else (or a throw) lets the upload proceed.
 */
export type HashGate = (id: string, file: Blob, result: HashResult) => Promise<'upload' | 'skip'>;

/** §3.2 step 4 — pool of `min(4, hardwareConcurrency - 1)` hashing workers, FIFO queue. */
export class HashPool {
  private workers: { w: Worker; busy: Job | null }[] = [];
  private queue: Job[] = [];
  private cancelled = new Set<string>();
  private gate: HashGate | null = null;
  constructor(private size = Math.max(1, Math.min(4, (navigator.hardwareConcurrency || 2) - 1))) {}

  private spawn(): { w: Worker; busy: Job | null } | null {
    if (typeof Worker === 'undefined') return null;
    try {
      const w = new Worker(new URL('./hash.worker.ts', import.meta.url), { type: 'module' });
      const slot = { w, busy: null as Job | null };
      w.onmessage = (e: MessageEvent<HashProgress | HashDone | HashFail>) => {
        const job = slot.busy;
        if (!job || e.data.id !== job.id) return;
        if ('done' in e.data) {
          slot.busy = null;
          void this.settle(job, { sha256: e.data.sha256, hash_kind: e.data.hash_kind });
          this.pump();
        } else if ('error' in e.data) {
          slot.busy = null;
          job.reject(new Error(e.data.error));
          this.pump();
        } else job.onProgress?.(e.data.progress);
      };
      w.onerror = (ev) => {
        const job = slot.busy;
        slot.busy = null;
        job?.reject(new Error(ev.message || 'hash worker error'));
        this.pump();
      };
      this.workers.push(slot);
      return slot;
    } catch {
      return null;
    }
  }

  hash(id: string, file: Blob, onProgress?: (p: number) => void): Promise<{ sha256: string; hash_kind: 'full' | 'sampled' }> {
    return new Promise((resolve, reject) => {
      this.queue.push({ id, file, onProgress, resolve, reject });
      this.pump();
    });
  }

  /** Install (or clear) the post-hash gate; one gate at a time. */
  setGate(gate: HashGate | null): void {
    this.gate = gate;
  }

  private async settle(job: Job, result: HashResult): Promise<void> {
    const gate = this.gate;
    if (!gate) return job.resolve(result);
    const choice = await gate(job.id, job.file, result).catch((): 'upload' => 'upload');
    if (choice === 'skip') job.reject(new Error('cancelled'));
    else job.resolve(result);
  }

  cancel(id: string): void {
    this.cancelled.add(id);
    const i = this.queue.findIndex((j) => j.id === id);
    if (i >= 0) {
      const [j] = this.queue.splice(i, 1);
      j!.reject(new Error('cancelled'));
    }
  }

  private pump(): void {
    while (this.queue.length) {
      let slot = this.workers.find((s) => !s.busy);
      if (!slot && this.workers.length < this.size) slot = this.spawn() ?? undefined;
      if (!slot) {
        if (this.workers.length === 0) {
          // No Worker support (tests, very old browsers): hash on the main thread.
          const job = this.queue.shift()!;
          void import('./hash.worker').then(({ hashBlob }) => hashBlob(job.file, job.onProgress).then((r) => this.settle(job, r), job.reject));
          continue;
        }
        return;
      }
      const job = this.queue.shift()!;
      if (this.cancelled.has(job.id)) {
        this.cancelled.delete(job.id);
        job.reject(new Error('cancelled'));
        continue;
      }
      slot.busy = job;
      slot.w.postMessage({ id: job.id, file: job.file });
    }
  }
}

let pool: HashPool | null = null;
export function hashPool(): HashPool {
  return (pool ??= new HashPool());
}
